import type { ResolvedUserScope, RoleScope, UserScope } from '@klareospace/pharos-lib-types';
import { ROLE_SCOPE } from '@klareospace/pharos-lib-types';
import { deduplicate } from './utils';

/**
 * Filter scopes by level and extract their IDs.
 */
export function getScopeIdsByLevel(scopes: readonly UserScope[], level: RoleScope): string[] {
  return scopes.filter((s) => s.level === level).map((s) => s.id);
}

/**
 * Filter scopes by level.
 */
export function getScopesByLevel<T extends UserScope>(scopes: readonly T[], level: RoleScope): T[] {
  return scopes.filter((s) => s.level === level);
}

/**
 * Build a UserScope array from separate space and site ID arrays.
 * Inverse of getScopeIdsByLevel.
 */
export function buildScopesFromIds(spaceIds: readonly string[], siteIds: readonly string[]): UserScope[] {
  const spaceScopes: UserScope[] = spaceIds.map((id) => ({ id, level: ROLE_SCOPE.SPACE }));
  const siteScopes: UserScope[] = siteIds.map((id) => ({ id, level: ROLE_SCOPE.SITE }));
  return [...spaceScopes, ...siteScopes];
}

/**
 * Extract unique parent space IDs from site-level resolved scopes.
 */
export function getParentSpaceIdsFromResolvedScopes(resolvedScopes: readonly ResolvedUserScope[]): string[] {
  const parentIds = resolvedScopes
    .filter((s): s is ResolvedUserScope & { parentSpaceId: string } => s.level === ROLE_SCOPE.SITE && !!s.parentSpaceId)
    .map((s) => s.parentSpaceId);
  return deduplicate(parentIds);
}

/**
 * Builds a spaceId -> space name map from resolved scopes (space scopes by id, site scopes via
 * their parent space); unnamed entries are skipped.
 */
export function getSpaceLabelsFromResolvedScopes(resolvedScopes: readonly ResolvedUserScope[]): Map<string, string> {
  const labels = new Map<string, string>();
  for (const scope of resolvedScopes) {
    if (scope.level === ROLE_SCOPE.SPACE && scope.name) {
      labels.set(scope.id, scope.name);
    }
    if (scope.level === ROLE_SCOPE.SITE && scope.parentSpaceId && scope.parentSpaceName) {
      labels.set(scope.parentSpaceId, scope.parentSpaceName);
    }
  }
  return labels;
}
